/**
 * useSession — top-level session orchestration hook.
 *
 * Composes:
 *   - useSessionSocket (WebSocket transport + auto-reconnect)
 *   - useTranscribe (mic → Transcribe pipeline)
 *   - useAudioPlayback (AudioPlaybackQueue for Polly audio)
 *   - useSessionStore (Pattern 6 state machine)
 *   - usePageVisibility (pause on tab hide, resume on show)
 *
 * Pitfall B mitigation: 5-minute heartbeat via setInterval in this hook.
 * Pitfall H mitigation: Page Visibility pause/resume wired here.
 * [BEGIN_SESSION] sentinel: sent on first WebSocket open for new sessions.
 *
 * Exposed API:
 *   { mode, turns, streamingAiText, partialTranscript, error,
 *     startMic, stopMic, sendTextTurn, endSession, resumeAudioContext }
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ReadyState, useSessionSocket } from '../lib/sessionWebsocket';
import { useTranscribe } from './useTranscribe';
import { useAudioPlayback } from './useAudioPlayback';
import { useSessionStore, newTurnId } from '../store/sessionStore';
import type { Turn } from '../store/sessionStore';
import { usePageVisibility } from './usePageVisibility';
import { newSessionId } from '../lib/idGenerator';

// ── Constants ─────────────────────────────────────────────────────────────────

/** 5-minute heartbeat interval (Pitfall B mitigation) */
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 300_000

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UseSessionOptions {
  sessionId: string;
  mode: 'voice' | 'text';
  preloadedTurns?: Turn[];
  onEnded?: () => void;
  /**
   * Optional photoId for photo-prompted sessions (Plan 02-05).
   * When present, the first user_turn frame uses [BEGIN_SESSION_PHOTO] sentinel
   * instead of [BEGIN_SESSION], and includes photoId in the frame.
   */
  photoId?: string;
  /**
   * Optional life area ID chosen from the pre-session suggestion card (Phase 7).
   * When set, included in the [BEGIN_SESSION] sentinel as a soft focus hint.
   * The backend buildSystemPrompt uses this to prime the AI with the chosen area.
   */
  focusAreaId?: string;
  /**
   * Optional story topic title chosen from the pre-session suggestion (Phase 30).
   * When set, included in the [BEGIN_SESSION] sentinel as a strong focus instruction.
   * Only injected once — on new session open. Session resume does not re-inject
   * (sentinelSent.current guard skips it). This is expected behavior.
   */
  topicTitle?: string;
}

export interface UseSessionReturn {
  mode: ReturnType<typeof useSessionStore.getState>['mode'];
  turns: Turn[];
  streamingAiText: string;
  partialTranscript: string;
  error: ReturnType<typeof useSessionStore.getState>['error'];
  /** True while Transcribe is actively streaming (voice mode) */
  isListening: boolean;
  /** Voice mode only: resume AudioContext (iOS Safari gesture requirement) then start mic */
  startMic: () => Promise<void>;
  /** Voice mode only: stop mic */
  stopMic: () => Promise<void>;
  /** Text mode: send a user turn, optionally with attached photo IDs, previews, or a document */
  sendTextTurn: (text: string, photoIds?: string[], mediaPreviews?: string[], doc?: { content: string; filename: string }) => void;
  /** End session, close WebSocket, reset store */
  endSession: () => void;
  /** iOS Safari: must be called from user gesture before first voice turn */
  resumeAudioContext: () => Promise<void>;
  /** True when a sent user turn has not yet been answered — retryTurn can re-send it */
  hasPendingTurn: boolean;
  /** Re-send the last unanswered user turn after an error (reconnects if needed) */
  retryTurn: () => void;
  /** True while AI voice audio is actively playing in the browser */
  isAiAudioPlaying: boolean;
  /** Stop AI audio playback immediately and hand the turn to the user (local only) */
  interruptAi: () => void;
  /** Voice mode: queue uploaded photo IDs (+ preview URLs) to ride along with the next voice turn */
  setVoiceMedia: (photoIds: string[], previews: string[]) => void;
  /** Increments each time queued voice media was sent with a turn — UI clears its chips on change */
  voiceMediaSentCount: number;
}

// ── getIdToken helper ─────────────────────────────────────────────────────────

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No id_token — user is not authenticated');
  return token;
}

// ── useSession ────────────────────────────────────────────────────────────────

export function useSession(opts: UseSessionOptions): UseSessionReturn {
  const { sessionId, mode, preloadedTurns, onEnded, photoId, focusAreaId, topicTitle } = opts;

  // ── Store ──────────────────────────────────────────────────────────────────
  const store = useSessionStore();
  const storeRef = useRef(store);
  storeRef.current = store;

  // Accumulates Transcribe final segments across the current speaking turn.
  // Sent to Claude in one batch when the user stops (silence timeout or manual stop).
  const transcriptBufferRef = useRef('');

  // Last user_turn frame sent — kept until the AI answers (ai_done) so a failed
  // turn can be re-sent via retryTurn() without losing the user's input.
  const lastTurnFrameRef = useRef<string | null>(null);
  // Frame queued for re-send once the socket is open and the store has settled.
  const pendingRetryRef = useRef<string | null>(null);
  // Set when the user interrupts AI playback — suppresses the rest of the
  // current turn's audio (and mode flips) until ai_done or the next user turn.
  const interruptedRef = useRef(false);
  // Voice mode: photos queued by the UI to ride along with the next voice turn.
  const voiceMediaRef = useRef<{ photoIds: string[]; previews: string[] }>({ photoIds: [], previews: [] });
  const [voiceMediaSentCount, setVoiceMediaSentCount] = useState(0);

  const setVoiceMedia = useCallback((photoIds: string[], previews: string[]) => {
    voiceMediaRef.current = { photoIds, previews };
  }, []);

  // ── Initialise store on mount ─────────────────────────────────────────────
  const initialized = useRef(false);
  if (!initialized.current) {
    initialized.current = true;
    store.setSessionId(sessionId, mode);
    if (preloadedTurns && preloadedTurns.length > 0) {
      // Hydrate turns from server — add each one individually
      for (const turn of preloadedTurns) {
        store.addTurn(turn);
      }
    }
    store.setMode('connecting');
  }

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const wsEnabled =
    store.mode !== 'idle' && store.mode !== 'paused';

  const wsUrl = import.meta.env.VITE_WSS_URL ?? '';
  const restApiUrl = import.meta.env.VITE_REST_API_URL ?? '';

  const { send, lastJsonMessage, readyState } = useSessionSocket({
    wsUrl,
    sessionId,
    getIdToken,
    enabled: wsEnabled,
  });

  // ── Audio playback ─────────────────────────────────────────────────────────
  const {
    enqueueChunk,
    onAudioDone: flushAudioQueue,
    resumeAudioContext,
    reset: resetAudio,
    isPlaying,
  } = useAudioPlayback();

  // ── Transcribe ─────────────────────────────────────────────────────────────
  const transcribe = useTranscribe({ getIdToken, restApiUrl });

  // ── Page Visibility (Pitfall H) ────────────────────────────────────────────
  const { isHidden } = usePageVisibility();
  const prevIsHidden = useRef(false);

  useEffect(() => {
    const wasHidden = prevIsHidden.current;
    prevIsHidden.current = isHidden;

    if (isHidden && !wasHidden) {
      // Tab became hidden — pause session
      const currentMode = storeRef.current.mode;
      if (
        currentMode !== 'idle' &&
        currentMode !== 'paused' &&
        currentMode !== 'error'
      ) {
        // Send pause frame to server
        if (readyState === ReadyState.OPEN) {
          send(JSON.stringify({ action: 'pause', sessionId }));
        }
        storeRef.current.pause();
        resetAudio();
      }
    } else if (!isHidden && wasHidden) {
      // Tab became visible — resume if we were paused
      if (storeRef.current.mode === 'paused') {
        storeRef.current.resume();
        // resume() sets mode='connecting' which re-enables the socket via wsEnabled
      }
    }
  }, [isHidden, readyState, send, sessionId, resetAudio]);

  // ── [BEGIN_SESSION] / [BEGIN_SESSION_PHOTO] on first connect ────────────
  // Pitfall 3 / welcome greeting: sentinel sent on first WebSocket open for new sessions.
  // When photoId is present (photo-prompted session), send [BEGIN_SESSION_PHOTO] + photoId.
  const sentinelSent = useRef(false);

  useEffect(() => {
    if (readyState !== ReadyState.OPEN || storeRef.current.mode !== 'connecting') return;

    if (storeRef.current.turns.length > 0) {
      // Resuming existing session — skip sentinel, go straight to gary_speaking
      storeRef.current.setMode('gary_speaking');
    } else if (!sentinelSent.current) {
      sentinelSent.current = true;
      if (photoId) {
        send(JSON.stringify({
          action: 'user_turn',
          sessionId,
          transcript: '[BEGIN_SESSION_PHOTO]',
          mode,
          photoId,
          ...(focusAreaId ? { focusAreaId } : {}),
          ...(topicTitle ? { topicTitle } : {}),
        }));
      } else {
        send(JSON.stringify({
          action: 'user_turn',
          sessionId,
          transcript: '[BEGIN_SESSION]',
          mode,
          ...(focusAreaId ? { focusAreaId } : {}),
          ...(topicTitle ? { topicTitle } : {}),
        }));
      }
    }
  }, [readyState, sessionId, mode, photoId, focusAreaId, topicTitle, send]);

  // ── WebSocket message dispatcher ──────────────────────────────────────────
  useEffect(() => {
    if (!lastJsonMessage) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const msg = lastJsonMessage as Record<string, any>;

    switch (msg.type) {
      case 'ai_text_delta': {
        storeRef.current.appendAiDelta(msg.text as string);
        // Ensure we transition to ai_speaking when first delta arrives
        if (
          !interruptedRef.current &&
          (storeRef.current.mode === 'connecting' ||
            storeRef.current.mode === 'processing' ||
            storeRef.current.mode === 'gary_speaking')
        ) {
          storeRef.current.setMode('ai_speaking');
        }
        break;
      }
      case 'audio_chunk': {
        if (!interruptedRef.current) {
          enqueueChunk(msg.data as string);
        }
        break;
      }
      case 'audio_done': {
        // Polly audio stream complete — trigger queue drain
        if (!interruptedRef.current) {
          flushAudioQueue();
        }
        // In voice mode, re-enable mic after audio finishes
        // (The onEnded callback from AudioPlaybackQueue will fire when queue drains)
        // We transition to gary_speaking in ai_done for voice mode
        break;
      }
      case 'ai_done': {
        const wasInterrupted = interruptedRef.current;
        interruptedRef.current = false;
        lastTurnFrameRef.current = null;
        const turnId = newTurnId();
        storeRef.current.finalizeAiTurn(turnId);
        // In text mode, enable input. In voice mode, mic re-enables.
        const currentMode = storeRef.current.mode;
        if (
          currentMode === 'ai_speaking' ||
          currentMode === 'processing' ||
          currentMode === 'connecting'
        ) {
          storeRef.current.setMode('gary_speaking');
        }
        // Voice mode: arm auto-listen. If audio already drained, start mic after state settles.
        // Skip when the user interrupted playback — they already have the turn.
        if (mode === 'voice' && !wasInterrupted) {
          autoListenRef.current = true;
          if (!isPlayingRef.current && !isListeningRef.current) {
            autoListenRef.current = false;
            setTimeout(() => { void startMicRef.current(); }, 0);
          }
        }
        break;
      }
      case 'error': {
        storeRef.current.setError({
          code: (msg.code as string) ?? 'UNKNOWN',
          message: (msg.message as string) ?? 'An error occurred',
        });
        break;
      }
      case 'turn_saved': {
        // Optional — currently no UI use
        break;
      }
    }
  }, [lastJsonMessage, enqueueChunk, flushAudioQueue]);

  // ── 5-minute heartbeat (Pitfall B) ────────────────────────────────────────
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    heartbeatRef.current = setInterval(() => {
      if (readyState === ReadyState.OPEN) {
        send(JSON.stringify({ action: 'heartbeat' }));
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [readyState, send]);

  // ── Reset store on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      storeRef.current.reset();
    };
  }, []);

  // ── Exposed actions ───────────────────────────────────────────────────────

  const startMic = useCallback(async () => {
    if (storeRef.current.mode !== 'gary_speaking') return;
    // Clear buffer and dedup guard for the new speaking turn
    transcriptBufferRef.current = '';
    prevFinal.current = null;
    // iOS Safari: resume AudioContext from user gesture BEFORE mic start
    await resumeAudioContext();
    await transcribe.start();
  }, [resumeAudioContext, transcribe]);

  const stopMic = useCallback(async () => {
    await transcribe.stop();

    const accumulated = (
      transcriptBufferRef.current ||
      transcribe.final ||
      transcribe.partial
    ).trim();
    transcriptBufferRef.current = '';

    if (accumulated && readyState === ReadyState.OPEN && storeRef.current.mode === 'gary_speaking') {
      const media = voiceMediaRef.current;
      const turnId = newSessionId();
      const turn: Turn = {
        id: turnId,
        role: 'user',
        content: accumulated,
        createdAt: new Date().toISOString(),
        ...(media.previews.length ? { mediaPreviews: media.previews } : {}),
      };
      storeRef.current.addTurn(turn);
      storeRef.current.clearPartialTranscript();
      storeRef.current.setMode('processing');

      const frame = JSON.stringify({
        action: 'user_turn',
        sessionId,
        transcript: accumulated,
        mode: 'voice',
        ...(media.photoIds.length ? { mediaPhotoIds: media.photoIds } : {}),
      });
      lastTurnFrameRef.current = frame;
      interruptedRef.current = false;
      send(frame);
      if (media.photoIds.length) {
        voiceMediaRef.current = { photoIds: [], previews: [] };
        setVoiceMediaSentCount(c => c + 1);
      }
    } else {
      storeRef.current.clearPartialTranscript();
    }
  }, [transcribe, readyState, sessionId, send]);

  // Keep stable refs to startMic/stopMic for use inside effects without stale closure issues
  const startMicRef = useRef(startMic);
  startMicRef.current = startMic;
  const stopMicRef = useRef(stopMic);
  stopMicRef.current = stopMic;

  // ── Auto-listen after AI audio ends (voice mode) ──────────────────────────
  // Armed by ai_done; fires when isPlaying transitions true → false (audio drained).
  const autoListenRef = useRef(false);
  const isPlayingRef = useRef(false);
  const isListeningRef = useRef(false);

  useEffect(() => { isListeningRef.current = transcribe.isListening; }, [transcribe.isListening]);

  useEffect(() => {
    const wasPlaying = isPlayingRef.current;
    isPlayingRef.current = isPlaying;
    if (wasPlaying && !isPlaying && autoListenRef.current && mode === 'voice') {
      autoListenRef.current = false;
      if (!isListeningRef.current) {
        void startMicRef.current();
      }
    }
  }, [isPlaying, mode]);

  // ── Silence auto-stop (voice mode) ───────────────────────────────────────
  // Calls stopMic() after 12 s of silence while mic is open.
  // Timer resets whenever partial transcript activity is detected.
  const VOICE_SILENCE_TIMEOUT_MS = 12_000;
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mode !== 'voice' || !transcribe.isListening) {
      if (silenceTimerRef.current !== null) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      return;
    }
    if (silenceTimerRef.current !== null) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => { void stopMicRef.current(); }, VOICE_SILENCE_TIMEOUT_MS);
    return () => {
      if (silenceTimerRef.current !== null) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribe.partial, transcribe.isListening, mode]);

  // Accumulate Transcribe finals into transcriptBufferRef — do NOT send yet.
  // The full accumulated transcript is sent to Claude when stopMic() fires
  // (either by the 12s silence timer or the user tapping the mic button).
  const prevFinal = useRef<string | null>(null);
  useEffect(() => {
    const { final } = transcribe;
    if (final && final !== prevFinal.current && final.trim().length > 0) {
      prevFinal.current = final;
      transcriptBufferRef.current = transcriptBufferRef.current
        ? `${transcriptBufferRef.current} ${final.trim()}`
        : final.trim();
      // Show accumulated text in the partial display so Gary can see what's captured
      storeRef.current.setPartialTranscript(transcriptBufferRef.current);
    }
  }, [transcribe.final]);

  // Wire in-progress partials → store (show accumulated text + current partial)
  useEffect(() => {
    if (!transcribe.partial) return; // finals clear partial — handled by the final effect above
    const accumulated = transcriptBufferRef.current;
    storeRef.current.setPartialTranscript(
      accumulated ? `${accumulated} ${transcribe.partial}` : transcribe.partial,
    );
  }, [transcribe.partial]);

  const sendTextTurn = useCallback((text: string, photoIds?: string[], mediaPreviews?: string[], doc?: { content: string; filename: string }) => {
    if (storeRef.current.mode !== 'gary_speaking') return;
    if (!text.trim() && !photoIds?.length && !doc) return;

    const turnId = newSessionId();
    const turn: Turn = {
      id: turnId,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
      ...(mediaPreviews?.length ? { mediaPreviews } : {}),
      ...(doc ? { documentFilename: doc.filename } : {}),
    };
    storeRef.current.addTurn(turn);
    storeRef.current.setMode('processing');

    const frame = JSON.stringify({
      action: 'user_turn',
      sessionId,
      transcript: text,
      mode: 'text',
      ...(photoIds?.length ? { mediaPhotoIds: photoIds } : {}),
      ...(doc ? { documentContent: doc.content, documentFilename: doc.filename } : {}),
    });
    lastTurnFrameRef.current = frame;
    interruptedRef.current = false;
    send(frame);
  }, [sessionId, send]);

  // ── Interrupt AI playback (Task: barge-in) ────────────────────────────────
  // Stops local audio immediately and hands the turn to the user. No server
  // message is needed — the turn's text has already been received/streamed.
  const interruptAi = useCallback(() => {
    interruptedRef.current = true;
    autoListenRef.current = false;
    resetAudio();
    if (storeRef.current.mode === 'ai_speaking') {
      storeRef.current.setMode('gary_speaking');
    }
  }, [resetAudio]);

  // ── Per-turn retry (turn-level error recovery) ────────────────────────────
  // retryTurn() queues the last unanswered user_turn frame and kicks the store
  // out of error mode; the effect below re-sends once the socket is open.
  const retryTurn = useCallback(() => {
    const frame = lastTurnFrameRef.current;
    if (!frame) return;
    pendingRetryRef.current = frame;
    if (storeRef.current.mode === 'error') {
      storeRef.current.resume();
    }
  }, []);

  useEffect(() => {
    if (!pendingRetryRef.current || readyState !== ReadyState.OPEN) return;
    const currentMode = useSessionStore.getState().mode;
    if (currentMode !== 'connecting' && currentMode !== 'gary_speaking') return;
    const frame = pendingRetryRef.current;
    pendingRetryRef.current = null;
    if (currentMode === 'connecting') storeRef.current.setMode('gary_speaking');
    interruptedRef.current = false;
    send(frame);
    storeRef.current.setMode('processing');
  }, [readyState, store.mode, send]);

  const endSession = useCallback(() => {
    if (readyState === ReadyState.OPEN) {
      send(JSON.stringify({ action: 'end_session', sessionId }));
    }
    transcriptBufferRef.current = '';
    lastTurnFrameRef.current = null;
    pendingRetryRef.current = null;
    resetAudio();
    void transcribe.stop();
    storeRef.current.reset();
    onEnded?.();
  }, [readyState, send, sessionId, resetAudio, transcribe, onEnded]);

  return {
    mode: store.mode,
    turns: store.turns,
    streamingAiText: store.streamingAiText,
    partialTranscript: store.partialTranscript,
    error: store.error,
    isListening: transcribe.isListening,
    startMic,
    stopMic,
    sendTextTurn,
    endSession,
    resumeAudioContext,
    hasPendingTurn: lastTurnFrameRef.current !== null,
    retryTurn,
    isAiAudioPlaying: isPlaying,
    interruptAi,
    setVoiceMedia,
    voiceMediaSentCount,
  };
}
